import { ref, computed } from 'vue'

/**
 * Wraps the Web Speech API (SpeechRecognition / webkitSpeechRecognition)
 * in a Vue-friendly composable.
 *
 * Browser support:
 *   - Chrome / Edge / Chrome on Android & Android TV: webkitSpeechRecognition
 *   - Firefox: not supported — `supported` is `false` and start() is a no-op
 *   - Safari (desktop): partial; iOS Safari: limited
 *
 * Usage:
 *   const voice = useVoiceSearch()
 *   if (voice.supported.value) voice.start('en-US')
 *   watch(voice.transcript, (t) => console.log(t))
 *
 * Permission denied / mic errors flip `listening` back to false and surface
 * the underlying SpeechRecognitionErrorEvent through `error.value` so the
 * caller can show a toast or fall back to text input.
 *
 * Auto-stop:
 *   `continuous: false` causes the recognizer to end on its own after a
 *   natural pause. We additionally arm a 1.5s silence timer reset on every
 *   interim result so a long pause inside one phrase still finalizes the
 *   utterance promptly (Chrome's built-in pause threshold is much longer).
 */
export function useVoiceSearch() {
  const SR =
    (typeof window !== 'undefined' &&
      (window.SpeechRecognition || window.webkitSpeechRecognition)) ||
    null

  const supported = computed(() => !!SR)
  const listening = ref(false)
  const transcript = ref('')
  const error = ref(null)

  let recognizer = null
  let silenceTimer = null

  function clearSilenceTimer() {
    if (silenceTimer) {
      clearTimeout(silenceTimer)
      silenceTimer = null
    }
  }

  function start(lang = 'en-US') {
    if (!SR) return
    // If a previous session is still active, abort it cleanly first so the
    // new one can claim the mic without colliding.
    if (recognizer) {
      try { recognizer.abort() } catch { /* noop */ }
      recognizer = null
    }

    error.value = null
    transcript.value = ''

    try {
      recognizer = new SR()
    } catch (err) {
      error.value = err
      listening.value = false
      return
    }

    recognizer.lang = lang || 'en-US'
    recognizer.interimResults = true
    recognizer.continuous = false

    recognizer.onresult = (event) => {
      let text = ''
      for (let i = 0; i < event.results.length; i++) {
        text += event.results[i][0].transcript
      }
      transcript.value = text
      // Reset silence timer on every new interim result.
      clearSilenceTimer()
      silenceTimer = setTimeout(() => {
        // stop() finalizes the transcript and triggers the onend handler.
        try { recognizer && recognizer.stop() } catch { /* noop */ }
      }, 1500)
    }

    recognizer.onend = () => {
      listening.value = false
      clearSilenceTimer()
    }

    recognizer.onerror = (event) => {
      // SpeechRecognitionErrorEvent: { error: 'not-allowed' | 'no-speech' | ... }
      error.value = event
      listening.value = false
      clearSilenceTimer()
    }

    try {
      recognizer.start()
      listening.value = true
    } catch (err) {
      // Firefox throws if you call start() while another session is mid-flight,
      // and Chrome throws InvalidStateError if start() is called twice.
      error.value = err
      listening.value = false
    }
  }

  function stop() {
    if (recognizer) {
      try { recognizer.stop() } catch { /* noop */ }
    }
    clearSilenceTimer()
  }

  function abort() {
    if (recognizer) {
      try { recognizer.abort() } catch { /* noop */ }
    }
    listening.value = false
    clearSilenceTimer()
  }

  return { supported, listening, transcript, error, start, stop, abort }
}
